import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import useDimensions from 'react-cool-dimensions';
import { getRandomArbitraryNumberInRange, _round } from '@utils';
import SkeletonLoader from './SkeletonLoader';
/**
 *
 * @export
 * This is the default pre loader for widgets
 */
const DefaultLoader = (props) => {
    return _jsxs("div", { className: 'uxpcore_loader uxpcore_loader--default', children: [_jsxs("div", { className: "uxpcore_loader__header", children: [_jsx("div", { className: "uxpcore_loader__icon" }), _jsx("div", { className: "uxpcore_loader__text" })] }), _jsxs("div", { className: "uxpcore_loader__content", children: [_jsxs("div", { className: "uxpcore_loader__para", children: [_jsx("div", { className: "uxpcore_loader__text" }), _jsx("div", { className: "uxpcore_loader__text" })] }), _jsxs("div", { className: "uxpcore_loader__para", children: [_jsx("div", { className: "uxpcore_loader__text" }), _jsx("div", { className: "uxpcore_loader__text" })] }), _jsxs("div", { className: "uxpcore_loader__para", children: [_jsx("div", { className: "uxpcore_loader__text" }), _jsx("div", { className: "uxpcore_loader__text" })] })] }), props.children] });
};
/**
 *
 * @export
 * Bar chart loader
 */
const BarChartLoader = (props) => {
    const { width, height, entry, unobserve, observe } = useDimensions({
        onResize: (p) => { },
    });
    function renderBars() {
        let bw = 60;
        let count = (_round(width / bw)) - 1;
        let bars = [];
        for (let i = 0; i < count; i++)
            bars.push(_jsx("div", { className: "uxpcore_loader__bar" }, i));
        return bars;
    }
    return _jsxs("div", { className: 'uxpcore_loader uxpcore_loader--barchart', ref: observe, children: [_jsx("div", { className: "uxpcore_loader__chart-area", children: renderBars() }), props.children] });
};
/**
 *
 * @export
 * Donut chart loader
 */
const DonutChartLoader = (props) => {
    const { width, height, entry, unobserve, observe } = useDimensions({
        onResize: (p) => { },
    });
    let x = (width > height ? height : width) - 50;
    let styles = {
        '--preloaderDonutSize': `${x}px`,
    };
    return _jsxs("div", { className: 'uxpcore_loader uxpcore_loader--donut', ref: observe, children: [_jsx("div", { className: "uxpcore_loader__chart-area", style: styles }), props.children] });
};
/**
 *
 * @export
 * gauge loader - mimics RadialGauge component
 */
const GaugeLoader = (props) => {
    let [value, setValue] = React.useState(0);
    let timer = React.useRef(null);
    React.useEffect(() => {
        timer.current = setInterval(() => {
            setValue(_round(getRandomArbitraryNumberInRange(0, 100), 2));
        }, 2000);
        return () => {
            if (timer.current)
                clearInterval(timer.current);
        };
    }, []);
    const { width, height, observe } = useDimensions({
        onResize: (p) => { },
    });
    // Calculate dimensions like RadialGauge
    const minSvgWidth = 150;
    const svgWidth = width > minSvgWidth ? width : minSvgWidth;
    const radius = svgWidth / 3;
    const strokeWidth = radius * 0.11;
    const needleHeight = radius * 0.65;
    const needleWidth = strokeWidth * 1.5;
    // Calculate needle rotation based on value (0-100)
    const percentage = value / 100;
    const needleRotation = (240 * percentage - 120);
    // Create tick marks
    const markers = [0, 12.5, 25, 37.5, 50, 62.5, 75, 87.5, 100];
    const styles = {
        '--preloaderGaugeSize': `${svgWidth}px`,
        '--preloaderGaugeRadius': `${radius}px`,
        '--preloaderGaugeStrokeWidth': `${strokeWidth}px`,
        '--preloaderGaugeNeedleHeight': `${needleHeight}px`,
        '--preloaderGaugeNeedleWidth': `${needleWidth}px`,
        '--preloaderGaugeNeedleRotation': `${needleRotation}deg`
    };
    function renderTicks() {
        const startAngle = -120;
        const totalAngle = 240;
        const tickStartRadius = radius + strokeWidth;
        const tickLength = strokeWidth;
        return markers.map((markerValue, i) => {
            const percentage = markerValue / 100;
            const tickAngle = startAngle + (percentage * totalAngle);
            return (_jsx("div", { className: "uxpcore_loader__gauge-tick", style: {
                    width: `${i % 2 === 0 ? 4 : 2}px`,
                    height: `${tickLength}px`,
                    top: `${(svgWidth / 2) - tickStartRadius}px`,
                    left: `${(svgWidth / 2) - (i % 2 === 0 ? 2 : 1)}px`,
                    transformOrigin: `center ${tickStartRadius}px`,
                    transform: `rotate(${tickAngle}deg)`
                } }, `tick-${i}`));
        });
    }
    function renderNumbers() {
        const numberRadius = radius * 0.9;
        const fontSize = radius * 0.1;
        return markers.filter((_, i) => i % 2 === 0).map((markerValue, i) => {
            const actualIndex = i * 2;
            const percentage = markerValue / 100;
            const angle = 240 * percentage - 120;
            const angleInRadians = ((angle - 90) * Math.PI) / 180.0;
            const x = (svgWidth / 2) + numberRadius * Math.cos(angleInRadians);
            const y = (svgWidth / 2) + numberRadius * Math.sin(angleInRadians);
            return (_jsx("div", { className: "uxpcore_loader__gauge-number", style: {
                    left: `${x}px`,
                    top: `${y}px`,
                    fontSize: `${fontSize}px`,
                    transform: 'translate(-50%, -50%)'
                }, children: markerValue }, `number-${actualIndex}`));
        });
    }
    function renderArc() {
        const centerX = svgWidth / 2;
        const centerY = svgWidth / 2;
        const arcRadius = radius + strokeWidth / 2;
        const startAngle = -120;
        const endAngle = 120;
        // Helper function to convert polar to cartesian
        const polarToCartesian = (angle) => {
            const angleInRadians = ((angle - 90) * Math.PI) / 180.0;
            return {
                x: centerX + arcRadius * Math.cos(angleInRadians),
                y: centerY + arcRadius * Math.sin(angleInRadians)
            };
        };
        const start = polarToCartesian(startAngle);
        const end = polarToCartesian(endAngle);
        // Create arc path (large arc flag = 1 for 240 degrees)
        const pathData = [
            `M ${start.x} ${start.y}`,
            `A ${arcRadius} ${arcRadius} 0 1 1 ${end.x} ${end.y}`
        ].join(' ');
        return (_jsxs("svg", { className: "uxpcore_loader__gauge-arc-svg", width: svgWidth, height: svgWidth, children: [_jsx("defs", { children: _jsxs("linearGradient", { id: `gauge-gradient-${svgWidth}`, x1: "0%", y1: "0%", x2: "100%", y2: "0%", children: [_jsx("stop", { offset: "0%", stopColor: "var(--loaderPrimaryColor)" }), _jsx("stop", { offset: "33%", stopColor: "var(--loaderSecondaryColor)" }), _jsx("stop", { offset: "66%", stopColor: "var(--loaderPrimaryColor)" }), _jsx("stop", { offset: "100%", stopColor: "var(--loaderSecondaryColor)" })] }) }), _jsx("path", { d: pathData, fill: "none", stroke: `url(#gauge-gradient-${svgWidth})`, strokeWidth: strokeWidth, strokeLinecap: "round" })] }));
    }
    return _jsxs("div", { className: 'uxpcore_loader uxpcore_loader--gauge', ref: observe, children: [_jsxs("div", { className: "uxpcore_loader__gauge-container", style: styles, children: [renderArc(), _jsx("div", { className: "uxpcore_loader__gauge-ticks", children: renderTicks() }), _jsx("div", { className: "uxpcore_loader__gauge-numbers", children: renderNumbers() }), _jsx("div", { className: "uxpcore_loader__gauge-needle", children: _jsx("div", { className: "uxpcore_loader__gauge-needle-circle" }) })] }), props.children] });
};
/**
 *
 * @export
 * heatmap chart loader
 */
const HeatmapChartLoader = (props) => {
    let [counter, setCounter] = React.useState(0);
    const { width, height, entry, unobserve, observe } = useDimensions({
        onResize: (p) => { },
    });
    React.useEffect(() => {
        setInterval(() => {
            setCounter(prev => (prev += 1));
        }, 2000);
    }, []);
    let sw = 40;
    let rows = _round((height - sw) / sw) - 1;
    let cols = _round((width - 80) / sw) - 1;
    function renderCells() {
        // Get theme-aware loader colors from CSS custom properties
        const rootStyles = getComputedStyle(document.documentElement);
        let colors = [
            rootStyles.getPropertyValue('--loaderPrimaryColor').trim(),
            rootStyles.getPropertyValue('--loaderSecondaryColor').trim(),
            rootStyles.getPropertyValue('--loaderBackgroundColor').trim()
        ];
        let cells = [];
        for (let i = 0; i < rows; i++) {
            for (let j = 0; j < cols; j++) {
                let styles = {
                    '--preloaderHeatmapChartCellBG': colors[_round(getRandomArbitraryNumberInRange(0, 2))]
                };
                cells.push(_jsx("div", { className: "uxpcore_loader__cell", style: styles }, `cell-${j}-${i}`));
            }
        }
        return cells;
    }
    function renderLegend(type) {
        let count = rows;
        if (type == "cols")
            count = cols;
        let cells = [];
        for (let i = 0; i < count; i++)
            cells.push(_jsx("div", { className: `uxpcore_loader__legend-${type}` }, i));
        return cells;
    }
    let contStyles = {
        '--preloaderHeatmapChartCellsWidth': `${cols * sw}px`,
        '--preloaderHeatmapChartCellsHeight': `${rows * sw}px`
    };
    return _jsxs("div", { className: 'uxpcore_loader uxpcore_loader--heatmap', ref: observe, children: [_jsxs("div", { className: "uxpcore_loader__chart-area", children: [_jsxs("div", { className: "uxpcore_loader__top-legend", children: [renderLegend('cols'), " "] }), _jsxs("div", { className: "uxpcore_loader__left-legend", children: [renderLegend('rows'), " "] }), _jsx("div", { className: "uxpcore_loader__cells", style: contStyles, children: renderCells() })] }), props.children] });
};
/**
 * @export
 * Map tile loader component using SkeletonLoader
 */
const MapTileLoader = (props) => {
    const { width, height, observe } = useDimensions({
        onResize: (p) => { },
    });
    function renderMapElements() {
        const elements = [];
        // Render grid of map tiles
        const tileSize = 150;
        const cols = Math.ceil(width / tileSize) || 4;
        const rows = Math.ceil(height / tileSize) || 3;
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                elements.push(_jsx("div", { className: "uxpcore_loader__map-tile", style: {
                        left: `${col * tileSize}px`,
                        top: `${row * tileSize}px`,
                        width: `${tileSize}px`,
                        height: `${tileSize}px`
                    } }, `tile-${row}-${col}`));
            }
        }
        // Add road/path elements
        const roadCount = 8;
        for (let i = 0; i < roadCount; i++) {
            const isVertical = i % 2 === 0;
            elements.push(_jsx("div", { className: `uxpcore_loader__map-road ${isVertical ? 'vertical' : 'horizontal'}`, style: {
                    [isVertical ? 'left' : 'top']: `${(i / roadCount) * 100}%`,
                    animationDelay: `${i * 0.15}s`
                } }, `road-${i}`));
        }
        // Add building/landmark elements
        const buildingCount = 12;
        for (let i = 0; i < buildingCount; i++) {
            const left = getRandomArbitraryNumberInRange(5, 95);
            const top = getRandomArbitraryNumberInRange(5, 95);
            const size = getRandomArbitraryNumberInRange(20, 50);
            elements.push(_jsx("div", { className: "uxpcore_loader__map-building", style: {
                    left: `${left}%`,
                    top: `${top}%`,
                    width: `${size}px`,
                    height: `${size}px`,
                    animationDelay: `${i * 0.1}s`
                } }, `building-${i}`));
        }
        return elements;
    }
    return (_jsxs("div", { className: 'uxpcore_loader uxpcore_loader--maptile', ref: observe, children: [_jsx("div", { className: "uxpcore_loader__map-container", children: renderMapElements() }), props.children] }));
};
/**
 * @export
 * TreeView skeleton loader - mimics TreeView structure with skeleton nodes
 */
const TreeViewLoader = (props) => {
    const labelWidths = [120, 150, 100, 180, 130, 90]; // Varied widths for natural look
    function renderSkeletonNode(depth = 0, index = 0, hasChildren = false) {
        const animationDelay = (depth * 3 + index) * 0.05;
        const labelWidth = labelWidths[index % labelWidths.length];
        return (_jsxs("div", { style: {
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px'
            }, children: [_jsx("div", { style: { marginRight: '4px', flexShrink: 0 }, children: _jsx(SkeletonLoader, { width: "28px", height: "28px", additionalStyle: {
                            borderRadius: '50%',
                            animationDelay: `${animationDelay}s`
                        } }) }), _jsxs("div", { style: {
                        flexGrow: 1,
                        padding: '4px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }, children: [_jsx(SkeletonLoader, { width: "20px", height: "20px", additionalStyle: {
                                borderRadius: '4px',
                                flexShrink: 0,
                                animationDelay: `${animationDelay + 0.01}s`
                            } }), _jsx(SkeletonLoader, { width: `${labelWidth}px`, height: "16px", additionalStyle: {
                                borderRadius: '4px',
                                animationDelay: `${animationDelay + 0.02}s`
                            } })] })] }, `skeleton-node-${depth}-${index}`));
    }
    function renderSkeletonNodeWithChildren(depth, index, childCount = 2) {
        return (_jsxs("div", { children: [renderSkeletonNode(depth, index, true), _jsx("div", { style: { paddingLeft: '24px' }, children: Array.from({ length: childCount }).map((_, i) => renderSkeletonNode(depth + 1, i, false)) })] }, `skeleton-parent-${depth}-${index}`));
    }
    function renderCompactLoader() {
        return (_jsxs(_Fragment, { children: [renderSkeletonNode(0, 0), renderSkeletonNode(0, 1), renderSkeletonNode(0, 2)] }));
    }
    function renderFullLoader() {
        return (_jsxs(_Fragment, { children: [renderSkeletonNode(0, 0), renderSkeletonNodeWithChildren(0, 1, 3), renderSkeletonNode(0, 2)] }));
    }
    return (_jsxs("div", { className: 'uxpcore_loader uxpcore_loader--treeview', style: { padding: '10px' }, children: [props.compact ? renderCompactLoader() : renderFullLoader(), props.children] }));
};
export { DefaultLoader, BarChartLoader, DonutChartLoader, GaugeLoader, HeatmapChartLoader, MapTileLoader, TreeViewLoader };
